import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import NotFound from "@/pages/NotFound";
import { MAIN_SITE_URL } from "@/mocks/categories";
import { articles, type LText } from "@/mocks/articles";
import { ArticleSchema } from "@/components/feature/SeoSchema";

export default function Article({ slug: slugProp }: { slug?: string }) {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const slug = slugProp || params.slug || "";
  const lang = (i18n.language || "en").split("-")[0] as keyof LText;
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <NotFound />;
  }

  const text = (o: LText) => o[lang] || o.en;

  return (
    <>
      <ArticleSchema article={article} lang={lang} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="px-4 md:px-6 py-10 md:py-14 max-w-3xl mx-auto">
          <nav aria-label="breadcrumb" className="text-sm text-foreground-500">
            <ol className="flex items-center gap-2 flex-wrap">
              <li>
                <Link to="/" className="hover:text-primary-600 transition-colors">
                  {t("article.breadcrumbHome")}
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li className="text-foreground-700">{text(article.category)}</li>
            </ol>
          </nav>

          <header className="mt-6">
            <h1 className="font-heading text-3xl md:text-4xl font-bold leading-tight text-foreground-950">
              {text(article.title)}
            </h1>
            <div className="mt-4 flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {t("article.readTime")}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-refresh-line"></i>
                {t("article.updated")}
              </span>
            </div>
          </header>

          <div className="mt-8 h-52 md:h-72 rounded-2xl overflow-hidden border border-background-200">
            <img
              src={article.image}
              alt={text(article.title)}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mt-8 space-y-9 text-foreground-700 leading-relaxed">
            <p className="text-lg text-foreground-800">{text(article.intro)}</p>

            {article.sections.map((s) => (
              <section key={s.title.en}>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{text(s.title)}</h2>
                <p className="mt-3">{text(s.body)}</p>
              </section>
            ))}

            <section className="bg-background-100 rounded-2xl border border-background-200 p-6 md:p-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">{t("article.faqTitle")}</h2>
              <div className="mt-5 space-y-5">
                {article.faqs.map((f) => (
                  <div key={f.q.en}>
                    <h3 className="font-semibold text-foreground-900">{text(f.q)}</h3>
                    <p className="mt-1.5 text-foreground-600">{text(f.a)}</p>
                  </div>
                ))}
              </div>
            </section>

            <p>{text(article.conclusion)}</p>

            <div className="rounded-2xl border border-background-200 bg-secondary-50 p-6 md:p-8">
              <p className="font-heading text-lg font-semibold text-foreground-950">{t("article.ctaText")}</p>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  {t("article.ctaHome")}
                  <i className="ri-arrow-left-line"></i>
                </Link>
                <a
                  href={MAIN_SITE_URL}
                  target="_blank"
                  rel="noopener nofollow"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-accent-600 transition-colors cursor-pointer"
                >
                  {t("article.ctaStore")}
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>

            <div className="pt-2 border-t border-background-200">
              <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors">
                <i className="ri-arrow-left-line"></i>
                {t("article.backHome")}
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}